const DRAFT_PREFIX = "sporthouse:program-creation";
export const PROGRAM_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const PROGRAM_WIZARD_VERSION = 2;

// Use the same representation for recovery comparisons and hydration. Marking
// migrated drafts also makes normalization safe to repeat without shifting twice.
export const normalizeProgramDraft = (draft) => {
  if (!draft || draft.programWizardVersion === PROGRAM_WIZARD_VERSION) return draft;
  if (draft.programWizardVersion != null && draft.programWizardVersion !== 1) return draft;
  return {
    ...draft,
    programWizardVersion: PROGRAM_WIZARD_VERSION,
    ...(draft.currentStep > 5 ? { currentStep: draft.currentStep - 1 } : {}),
  };
};

export const getProgramDraftKey = ({ userId, orgId, programType }) =>
  [DRAFT_PREFIX, userId || "anonymous", orgId || "unassigned", programType || "recreational"].join(":");

export const isProgramDraftMeaningful = (programDetails) =>
  ["name", "sport", "start_date", "end_date", "registration_deadline", "location"]
    .some((field) => Boolean(programDetails?.[field]?.toString().trim()));

export const loadProgramDraft = (storage, key, now = Date.now()) => {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft.savedAt || now - draft.savedAt > PROGRAM_DRAFT_MAX_AGE_MS) {
      storage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

export const saveProgramDraft = (storage, key, data, now = Date.now()) => {
  try {
    storage?.setItem(key, JSON.stringify({ ...data, savedAt: now }));
    return now;
  } catch {
    return null;
  }
};

export const clearProgramDraft = (storage, key) => {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage may be disabled by browser privacy settings.
  }
};

const comparableDraft = (draft) => {
  if (!draft) return null;
  const { savedAt, updated_at, created_at, ...data } = normalizeProgramDraft(draft);
  return data;
};

const canonicalize = (value) => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== "object") return value;
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = canonicalize(value[key]);
    return result;
  }, {});
};

export const programDraftsDiverge = (localDraft, serverDraft) =>
  JSON.stringify(canonicalize(comparableDraft(localDraft))) !== JSON.stringify(canonicalize(comparableDraft(serverDraft)));

export const formatProgramDraftSavedAt = (value, locale) => {
  const date = typeof value === "number" ? new Date(value) : new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(date);
};
